/**
 * ThemeManager — manual override + OS fallback.
 *
 * Behaviour:
 *   - On load, checks localStorage for a saved manual preference. If set, use it.
 *   - Otherwise falls back to OS `prefers-color-scheme`.
 *   - OS changes are honoured ONLY while the user has not made a manual choice.
 *   - `toggle()` / `setManualTheme()` lock to that choice and persist it.
 *   - `resetToOs()` forgets the manual choice and re-follows OS preference.
 *
 * Public API on `window`:
 *   - `window.ThemeManager` — full instance
 *   - `window.AIM_toggleTheme()` — convenience for the navbar button
 */

export type ThemeName = 'aim-light' | 'aim-dark';

const STORAGE_KEY = 'aim2-theme-preference';

export class ThemeManager {
    private activeTheme: ThemeName = 'aim-light';
    private hasManualOverride: boolean = false;

    public initialize(): void {
        const saved = this.readSavedPreference();
        if (saved) {
            this.hasManualOverride = true;
            this.applyTheme(saved, /* persist */ false);
        } else {
            this.detectOsTheme();
        }
        this.listenForOsChanges();
    }

    private detectOsTheme(): void {
        const prefersDark: boolean = window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.applyTheme(prefersDark ? 'aim-dark' : 'aim-light', /* persist */ false);
    }

    private listenForOsChanges(): void {
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e: MediaQueryListEvent) => {
            // OS-driven changes are ignored once the user has set a manual preference.
            if (this.hasManualOverride) return;
            this.applyTheme(e.matches ? 'aim-dark' : 'aim-light', /* persist */ false);
        });
    }

    /** Manually set the theme. Locks out OS auto-switching and persists across reloads. */
    public setManualTheme(theme: ThemeName): void {
        this.hasManualOverride = true;
        this.applyTheme(theme, /* persist */ true);
    }

    /** Toggle between light and dark. Persists the choice. */
    public toggle(): void {
        this.setManualTheme(this.activeTheme === 'aim-light' ? 'aim-dark' : 'aim-light');
    }

    /** Forget any manual choice and revert to OS preference detection. */
    public resetToOs(): void {
        this.hasManualOverride = false;
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
        this.detectOsTheme();
    }

    private applyTheme(theme: ThemeName, persist: boolean): void {
        if (theme === 'aim-light') {
            document.body.removeAttribute('data-theme');
        } else {
            document.body.setAttribute('data-theme', theme);
        }
        this.activeTheme = theme;

        if (persist) {
            try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
        }

        this.dispatchChangeEvent();
    }

    private readSavedPreference(): ThemeName | null {
        try {
            const v = localStorage.getItem(STORAGE_KEY);
            return v === 'aim-light' || v === 'aim-dark' ? v : null;
        } catch {
            return null;
        }
    }

    private dispatchChangeEvent(): void {
        document.dispatchEvent(new CustomEvent('aim2-theme-changed', {
            detail: { theme: this.activeTheme, isDark: this.isDarkMode() },
            bubbles: true
        }));
    }

    public getActiveTheme(): ThemeName {
        return this.activeTheme;
    }

    public isDarkMode(): boolean {
        return this.activeTheme === 'aim-dark';
    }

    public hasManualPreference(): boolean {
        return this.hasManualOverride;
    }

    public getCssVariable(name: string): string {
        return getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
    }
}

// Auto-initialize and expose globally for backward compat + navbar onclick handler.
const themeManager: ThemeManager = new ThemeManager();

declare global {
    interface Window {
        ThemeManager: ThemeManager;
        AIM_toggleTheme: () => void;
    }
}
window.ThemeManager = themeManager;
window.AIM_toggleTheme = (): void => themeManager.toggle();

document.addEventListener('DOMContentLoaded', () => {
    themeManager.initialize();
});

export default themeManager;
